"use client";

import React, { useEffect, useState } from "react";
import { 
  Calendar, 
  Clock, 
  User, 
  ArrowLeft, 
  Share2, 
  Tag, 
  ChevronRight 
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardFooter 
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";


declare const wvcClient: any;



function RelatedPostsConsumer({ currentPostId }: { currentPostId: string }) {
  const { posts, loading, error } = usePosts();

  
  const relatedPosts = posts.filter(p => p.id !== currentPostId).slice(0, 3);

  if (loading) {
    return (
      <div data-section-id="35533" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} data-index={i} className="h-80 animate-pulse rounded-3xl bg-muted/30" />
        ))}
      </div>
    );
  }

  if (error || relatedPosts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {relatedPosts.map((post, i) => (
        <Card key={post.id} data-index={i} className="flex h-full flex-col overflow-hidden border-secondary/20 bg-card transition-all hover:border-primary/40 hover:shadow-xl rounded-3xl">
          <div className="relative aspect-video w-full overflow-hidden bg-accent/10">
            {post.featuredImage ? (
              <img 
                src={post.featuredImage} 
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                <span className="text-sm font-sans font-medium">Adventure Awaits</span>
              </div>
            )}
          </div>
          <CardHeader className="p-5 pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs font-sans text-secondary font-bold uppercase tracking-wider">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "New Discovery"}</span>
            </div>
            <CardTitle className="line-clamp-2 text-xl font-serif font-bold leading-tight text-foreground">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-2 grow">
            <p className="line-clamp-3 text-sm font-sans text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: post.excerpt || "" }} />
          </CardContent>
          <CardFooter className="p-5 pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full cursor-pointer justify-between group rounded-full border-primary/30 text-primary hover:bg-primary hover:text-white">
                Join the Fun
                <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection({ currentPostId }: { currentPostId: string }) {
  
  const wp_query = new WP_Query({
    posts_per_page: 4, 
    post_type: "post",
    ignore_sticky_posts: true,
  });

  return (
    <div className="mt-16 md:mt-24">
      <div className="mb-8 flex items-center justify-between">
        <h3 className="text-2xl font-serif font-bold tracking-tight text-secondary md:text-4xl">More Learning Adventures</h3>
        <Link to="#">
          <Button variant="ghost" className="hidden sm:flex font-sans font-bold text-primary hover:text-primary hover:bg-primary/10 rounded-full">Explore Our Blog</Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer currentPostId={currentPostId} />
      </PostsProvider>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 px-4 py-12 md:py-20">
        <div className="h-8 w-32 animate-pulse rounded-full bg-muted" />
        <div className="h-12 w-3/4 animate-pulse rounded-2xl bg-muted" />
        <div className="aspect-video w-full animate-pulse rounded-3xl bg-muted" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <h2 className="text-3xl font-serif font-bold text-secondary">Adventure paused!</h2>
        <p className="text-muted-foreground font-sans">We couldn't load this story right now.</p>
        <Link to="#">
          <Button className="rounded-full px-8">Return to Adventures</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <h2 className="text-3xl font-serif font-bold text-secondary">Story not found</h2>
        <p className="text-muted-foreground font-sans">This magical page seems to have disappeared!</p>
        <Link to="#">
          <Button className="rounded-full px-8">Return to Adventures</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "Teacher Thanu";
  const authorAvatar = post.author?.avatar?.["96"] || "";
  const publishDate = post.date ? new Date(post.date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  }) : "";

  return (
    <article className="container mx-auto max-w-4xl px-4 py-12 md:py-20">
      {}
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <Link to="#">
          <Button variant="ghost" size="sm" className="-ml-2 font-sans font-bold text-secondary hover:text-primary hover:bg-transparent">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to All Stories
          </Button>
        </Link>
        <div className="flex items-center gap-2">
          {post.categories && post.categories.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-accent/20 px-4 py-1.5 text-xs font-bold text-accent-foreground uppercase tracking-wide font-sans">
              {post.categories[0].name}
            </span>
          )}
        </div>
      </div>

      {}
      <header className="mb-10 space-y-6 text-center md:text-left">
        <h1 className="text-4xl font-serif font-bold tracking-tight text-primary sm:text-5xl md:text-6xl lg:text-7xl leading-tight">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-sans font-medium text-muted-foreground md:justify-start">
          <div className="flex items-center gap-2">
            <Avatar className="h-10 w-10 border-2 border-secondary/20 shadow-sm">
              <AvatarImage src={authorAvatar} alt={authorName} />
              <AvatarFallback className="bg-secondary/10 text-secondary"><User className="h-5 w-5" /></AvatarFallback>
            </Avatar>
            <span className="font-bold text-foreground">{authorName}</span>
          </div>
          <Separator orientation="vertical" className="hidden h-4 md:block bg-border/50" />
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-secondary" />
            <span>{publishDate}</span>
          </div>
          <Separator orientation="vertical" className="hidden h-4 md:block bg-border/50" />
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-secondary" />
            <span>Quick Adventure</span>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-[2rem] border-4 border-white bg-muted shadow-xl">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div className="prose prose-lg prose-neutral dark:prose-invert mx-auto max-w-none md:mx-0 font-sans leading-relaxed text-foreground/90">
        <div dangerouslySetInnerHTML={{ __html: post.content || "" }} />
      </div>

      {}
      <div className="mt-12 flex flex-col gap-6 border-y border-border/50 py-8 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2">
          {post.tags && post.tags.map((tag, i) => (
            <div key={tag.id} data-index={i} className="flex items-center gap-1 rounded-full bg-secondary/10 px-3.5 py-1.5 text-sm font-bold text-secondary transition-colors hover:bg-secondary hover:text-white">
              <Tag className="h-3.5 w-3.5" />
              <span>{tag.name}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm font-bold text-muted-foreground font-sans">Share the joy:</span>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" className="h-10 w-10 rounded-full border-primary/20 text-primary hover:bg-primary hover:text-white">
              <Share2 className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {}
      <div className="mt-12">
        <Card className="overflow-hidden bg-accent/10 border-none shadow-none rounded-[2rem]">
          <CardContent className="flex flex-col gap-6 p-8 sm:flex-row sm:items-center">
            <Avatar className="h-24 w-24 border-4 border-white shadow-md">
              <AvatarImage src={authorAvatar} alt={authorName} />
              <AvatarFallback className="bg-primary/20 text-primary text-2xl font-serif">
                {authorName.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-2 text-center sm:text-left">
              <h3 className="text-2xl font-serif font-bold text-secondary">Meet {authorName}</h3>
              <p className="text-muted-foreground font-sans leading-relaxed">
                Language Explorer & Teacher. A passionate Spanish educator dedicated to making language learning a magical adventure for every child through songs, games, and stories.
              </p>
              <div className="pt-2">
                <Link to="#">
                  <Button variant="link" className="h-auto p-0 text-primary font-bold text-lg hover:no-underline hover:text-primary/80">
                    See more from {authorName}
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {}
      <RelatedPostsSection currentPostId={post.id} />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen w-full selection:bg-primary/20">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}